import { useState, useEffect, useCallback } from 'react';
import { Gamepad2, Trophy, RotateCcw, ChevronLeft, Pause, Play } from 'lucide-react';

interface Cell {
  value: number;
  id: number;
  isNew?: boolean;
  isMerged?: boolean;
}

export default function Game2048() {
  const [grid, setGrid] = useState<Cell[][]>([]);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(() => {
    return parseInt(localStorage.getItem('2048HighScore') || '0');
  });
  const [gameOver, setGameOver] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [nextId, setNextId] = useState(1);

  const SIZE = 4;

  const createEmptyGrid = useCallback((): Cell[][] => {
    return Array(SIZE).fill(null).map(() => Array(SIZE).fill(null));
  }, []);

  const getEmptyCells = useCallback((currentGrid: Cell[][]): { x: number; y: number }[] => {
    const empty: { x: number; y: number }[] = [];
    for (let y = 0; y < SIZE; y++) {
      for (let x = 0; x < SIZE; x++) {
        if (!currentGrid[y][x] || currentGrid[y][x].value === 0) {
          empty.push({ x, y });
        }
      }
    }
    return empty;
  }, []);

  const addRandomTile = useCallback((currentGrid: Cell[][], currentId: number): { grid: Cell[][]; id: number } => {
    const empty = getEmptyCells(currentGrid);
    if (empty.length === 0) return { grid: currentGrid, id: currentId };

    const { x, y } = empty[Math.floor(Math.random() * empty.length)];
    const newGrid = currentGrid.map(row => [...row]);
    newGrid[y][x] = {
      value: Math.random() < 0.9 ? 2 : 4,
      id: currentId,
      isNew: true
    };
    return { grid: newGrid, id: currentId + 1 };
  }, [getEmptyCells]);

  const initGame = useCallback(() => {
    let newGrid = createEmptyGrid();
    let currentId = 1;
    
    const result1 = addRandomTile(newGrid, currentId);
    newGrid = result1.grid;
    currentId = result1.id;
    
    const result2 = addRandomTile(newGrid, currentId);
    newGrid = result2.grid;
    currentId = result2.id;
    
    setGrid(newGrid);
    setNextId(currentId);
    setScore(0);
    setGameOver(false);
    setIsPaused(false);
    setGameStarted(true);
  }, [createEmptyGrid, addRandomTile]);

  const resetGame = () => {
    initGame();
  };

  const move = useCallback((direction: string) => {
    if (gameOver || isPaused || !gameStarted) return;

    let newGrid = grid.map(row => [...row]);
    let moved = false;
    let newScore = score;
    let currentId = nextId;

    const rotate = (g: Cell[][]): Cell[][] => {
      const rotated: Cell[][] = [];
      for (let x = 0; x < SIZE; x++) {
        rotated[x] = [];
        for (let y = SIZE - 1; y >= 0; y--) {
          rotated[x][SIZE - 1 - y] = g[y][x];
        }
      }
      return rotated;
    };

    const slide = (row: Cell[]): { row: Cell[]; score: number; moved: boolean } => {
      let filtered = row.filter(cell => cell && cell.value !== 0);
      let rowScore = 0;
      let hasMoved = filtered.length !== row.length;

      for (let i = 0; i < filtered.length - 1; i++) {
        if (filtered[i].value === filtered[i + 1].value) {
          filtered[i].value *= 2;
          filtered[i].isMerged = true;
          rowScore += filtered[i].value;
          filtered.splice(i + 1, 1);
          hasMoved = true;
        }
      }

      while (filtered.length < SIZE) {
        filtered.push({ value: 0, id: 0 });
      }

      return { row: filtered, score: rowScore, moved: hasMoved };
    };

    let rotations = 0;
    if (direction === 'up') rotations = 1;
    else if (direction === 'right') rotations = 2;
    else if (direction === 'down') rotations = 3;

    for (let i = 0; i < rotations; i++) {
      newGrid = rotate(newGrid);
    }

    for (let y = 0; y < SIZE; y++) {
      const result = slide(newGrid[y]);
      newGrid[y] = result.row;
      newScore += result.score;
      if (result.moved) moved = true;
    }

    for (let i = 0; i < (4 - rotations) % 4; i++) {
      newGrid = rotate(newGrid);
    }

    if (moved) {
      const result = addRandomTile(newGrid, currentId);
      newGrid = result.grid;
      currentId = result.id;
      
      setGrid(newGrid);
      setNextId(currentId);
      setScore(prev => {
        const newTotal = prev + newScore;
        if (newTotal > highScore) {
          setHighScore(newTotal);
          localStorage.setItem('2048HighScore', newTotal.toString());
        }
        return newTotal;
      });

      // Check game over
      const empty = getEmptyCells(newGrid);
      if (empty.length === 0) {
        let canMove = false;
        for (let y = 0; y < SIZE && !canMove; y++) {
          for (let x = 0; x < SIZE - 1 && !canMove; x++) {
            if (newGrid[y][x].value === newGrid[y][x + 1].value) canMove = true;
          }
        }
        for (let x = 0; x < SIZE && !canMove; x++) {
          for (let y = 0; y < SIZE - 1 && !canMove; y++) {
            if (newGrid[y][x].value === newGrid[y + 1][x].value) canMove = true;
          }
        }
        if (!canMove) setGameOver(true);
      }
    }
  }, [grid, gameOver, isPaused, gameStarted, score, nextId, addRandomTile, getEmptyCells, highScore]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!gameStarted) return;
      
      switch(e.key) {
        case 'ArrowUp': e.preventDefault(); move('up'); break;
        case 'ArrowDown': e.preventDefault(); move('down'); break;
        case 'ArrowLeft': e.preventDefault(); move('left'); break;
        case 'ArrowRight': e.preventDefault(); move('right'); break;
        case ' ': e.preventDefault(); setIsPaused(prev => !prev); break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [move, gameStarted]);

  const handleTouch = (dir: string) => {
    if (!gameStarted) {
      initGame();
      return;
    }
    move(dir);
  };

  const getCellColor = (value: number): string => {
    const colors: { [key: number]: string } = {
      0: 'bg-telegram-dark',
      2: 'bg-gray-600',
      4: 'bg-gray-500',
      8: 'bg-orange-400',
      16: 'bg-orange-500',
      32: 'bg-red-400',
      64: 'bg-red-500',
      128: 'bg-yellow-400',
      256: 'bg-yellow-500',
      512: 'bg-green-400',
      1024: 'bg-green-500',
      2048: 'bg-purple-500',
    };
    return colors[value] || 'bg-purple-600';
  };

  useEffect(() => {
    initGame();
  }, [initGame]);

  return (
    <div className="animate-fade-in">
      <div className="px-4 pt-4">
        {/* 标题栏 */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-telegram-text flex items-center">
            <Gamepad2 className="w-6 h-6 mr-2 text-orange-400" />
            2048
          </h2>
          <div className="flex items-center space-x-3">
            <div className="text-right">
              <div className="text-xs text-telegram-secondary">分数</div>
              <div className="text-lg font-bold text-orange-400">{score}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-telegram-secondary flex items-center">
                <Trophy className="w-3 h-3 mr-1" />
                最高
              </div>
              <div className="text-sm font-bold text-yellow-400">{highScore}</div>
            </div>
          </div>
        </div>

        {/* 游戏区域 */}
        <div className="relative bg-telegram-dark rounded-xl overflow-hidden border-2 border-orange-500/30 mb-4 p-2">
          {gameOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-20 rounded-lg">
              <div className="text-center p-4">
                <p className="text-red-400 font-bold text-2xl mb-2">游戏结束!</p>
                <p className="text-white mb-4">最终得分: {score}</p>
                <button
                  onClick={resetGame}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium flex items-center justify-center"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  重新开始
                </button>
              </div>
            </div>
          )}

          {isPaused && !gameOver && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10 rounded-lg">
              <Pause className="w-12 h-12 text-white" />
            </div>
          )}

          {/* 网格 */}
          <div className="grid grid-cols-4 gap-1">
            {grid.map((row, y) =>
              row.map((cell, x) => (
                <div
                  key={`${y}-${x}`}
                  className={`aspect-square rounded-lg flex items-center justify-center text-white font-bold transition-all duration-150 ${
                    getCellColor(cell?.value || 0)
                  } ${cell?.value && cell.value > 0 ? 'text-lg' : ''} ${
                    cell?.isNew ? 'animate-pulse' : ''
                  } ${cell?.isMerged ? 'animate-bounce' : ''}`}
                >
                  {cell?.value > 0 ? cell.value : ''}
                </div>
              ))
            )}
          </div>
        </div>

        {/* 移动端控制按钮 */}
        <div className="flex flex-col items-center space-y-2 mb-4">
          <button
            onClick={() => handleTouch('up')}
            className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-orange-500/30 transition-colors border border-orange-500/30"
          >
            <ChevronLeft className="w-8 h-8 text-white rotate-90" />
          </button>
          <div className="flex space-x-4">
            <button
              onClick={() => handleTouch('left')}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-orange-500/30 transition-colors border border-orange-500/30"
            >
              <ChevronLeft className="w-8 h-8 text-white" />
            </button>
            <button
              onClick={() => setIsPaused(!isPaused)}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-yellow-500/30 transition-colors border border-yellow-500/30"
            >
              {isPaused ? <Play className="w-6 h-6 text-yellow-400" /> : <Pause className="w-6 h-6 text-yellow-400" />}
            </button>
            <button
              onClick={() => handleTouch('right')}
              className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-orange-500/30 transition-colors border border-orange-500/30"
            >
              <ChevronLeft className="w-8 h-8 text-white rotate-180" />
            </button>
          </div>
          <button
            onClick={() => handleTouch('down')}
            className="w-14 h-14 bg-telegram-dark rounded-full flex items-center justify-center active:bg-orange-500/30 transition-colors border border-orange-500/30"
          >
            <ChevronLeft className="w-8 h-8 text-white -rotate-90" />
          </button>
        </div>

        {/* 操作说明 */}
        <div className="px-4 py-3 bg-telegram-dark rounded-xl">
          <p className="text-xs text-telegram-secondary text-center">
            💡 提示：滑动或点击方向键移动方块，相同数字会合并，目标是达到 2048！
          </p>
        </div>
      </div>
    </div>
  );
}
